import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import {
  Box,
  Typography,
} from '@mui/material';
import { AppBulkActions, AppPagination, EmployeeDisplay } from '@/__core/components';
import ConfirmationModal from '@/__core/components/ConfirmationModal';
import DataTableModule from '@/__core/layout/module/DataTableModule';
import { sharedListStyles } from '@/__core/styles/sharedListStyles';
import { softDeleteEmployees } from '../redux/EmployeeCRUD';
import { formatErrorForSnackbar } from '@/__core/helpers/Error';
import { useGlobalLoading } from '@/__core/hooks/useGlobalLoading';
import {
  EMPLOYEE_STATUS_MODULES,
  getStatusMetaByModule,
} from '@/__core/utils/statusByModuleCache';
import { resolveEmployeeStatusId } from '../utils/employeeStatus';
import { MISSIONS_OPTIONS } from '@/__core/constants/mission';


export interface EmployeeListRow {
  id: string;
  name: string;
  nickname?: string;
  avatar?: string | null;
  image1?: string | null;
  userId?: string;
  code: string;
  phone?: string;
  email: string;
  missions?: string[];
  startDate?: string | null;
  jobTitle: string;
  status: string;
  statusName?: string;
  statusColor?: string;
  attendance_type?: 'attendance' | 'no_attendance' | string;
  department: string;
  company: string;
}

export type MainListEmployeeHandle = {
  requestDelete: () => void;
};

interface MainListEmployeeProps {
  employees: EmployeeListRow[];
  activeCompanyFilter?: string;
  pagination?: {
    count: number;
    page: number;
    pageSize: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  onPageChange?: (event: React.ChangeEvent<unknown>, page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onRowClick: (employee: EmployeeListRow) => void;
  onReadDenied?: (message: string) => void;
  onSelectionChange?: (selectedIds: string[]) => void;
  onDeleted?: () => void;
  onDeleteSuccess?: (message: string) => void;
  onDeleteError?: (message: string) => void;
  canDelete?: boolean;
  canRead?: boolean;
}

type StatusMeta = {
  name: string;
  color?: string;
};

type EmployeeGroupRow = {
  id: string;
  __hideCheckbox: true;
  __groupRow: true;
  __renderGroupCells: true;
  image1?: string;
  name: string;
  departmentLabel: string;
  rowCount: number;
};

type EmployeeDataRow = EmployeeListRow | EmployeeGroupRow;

const isGroupRow = (row: EmployeeDataRow): row is EmployeeGroupRow =>
  (row as EmployeeGroupRow).__groupRow === true;

const MainListEmployee = forwardRef<MainListEmployeeHandle, MainListEmployeeProps>(function MainListEmployee(
  {
    employees,
    activeCompanyFilter = 'all',
    pagination,
    onPageChange,
    onPageSizeChange,
    onRowClick,
    onReadDenied,
    onSelectionChange,
    onDeleted,
    onDeleteSuccess,
    onDeleteError,
    canDelete: canDeleteProp,
    canRead: canReadProp,
  },
  ref
) {
  const { startGlobalLoading, stopGlobalLoading } = useGlobalLoading();
  const effectiveCanDelete = canDeleteProp === true;
  const effectiveCanRead = canReadProp !== false;

  const displayEmployees = useMemo(() => {
    if (!employees?.length) return [];
    if (activeCompanyFilter && activeCompanyFilter !== 'all') {
      return employees;
    }
    return employees;
  }, [employees, activeCompanyFilter]);

  const groupedEmployees = useMemo(() => {
    const groups = new Map<string, EmployeeListRow[]>();
    const noDeptLabel = 'Khong co phong ban';

    displayEmployees.forEach((employee) => {
      const department = String(employee.department || '').trim() || noDeptLabel;
      if (!groups.has(department)) {
        groups.set(department, []);
      }
      groups.get(department)!.push(employee);
    });

    return Array.from(groups.entries())
      .map(([department, rows]) => ({ department, rows }))
      .sort((a, b) => {
        if (a.department === noDeptLabel) return 1;
        if (b.department === noDeptLabel) return -1;
        return a.department.localeCompare(b.department, 'vi');
      });
  }, [displayEmployees]);

  const tableRows = useMemo<EmployeeDataRow[]>(() => {
    const rows: EmployeeDataRow[] = [];

    groupedEmployees.forEach((group) => {
      rows.push({
        id: `group-${group.department}`,
        __hideCheckbox: true,
        __groupRow: true,
        __renderGroupCells: true,
        image1: '',
        name: group.department,
        departmentLabel: group.department,
        rowCount: group.rows.length,
      });

      group.rows.forEach((employee) => rows.push(employee));
    });

    return rows;
  }, [groupedEmployees]);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState('');
  const [applyingAction] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [statusMetaById, setStatusMetaById] = useState<Record<string, StatusMeta>>({});

  const renderFallback = () => <Typography sx={sharedListStyles.cellText}>-</Typography>;

  const formatStartDate = (value?: string | null) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };


  const getStatusKey = (row: EmployeeListRow): string => {
    return resolveEmployeeStatusId(
      row.status ?? (row as any)?.status_id,
      ''
    );
  };

  const isLikelyId = (value: string): boolean => {
    const trimmed = (value || '').trim();
    if (!trimmed) return false;
    const uuidLike = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidLike.test(trimmed);
  };

  const getStatusLabel = (row: EmployeeListRow): string => {
    const directName = String(row.statusName || '').trim();
    if (directName) {
      return directName;
    }

    const nestedName =
      typeof (row as any)?.status === 'object'
        ? String((row as any).status?.name || '').trim()
        : '';
    if (nestedName) {
      return nestedName;
    }

    const statusKey = getStatusKey(row);
    const mappedName = statusMetaById[statusKey]?.name;
    if (mappedName) {
      return mappedName;
    }
    if (statusKey === 'active') return 'Hoạt động';
    if (statusKey === 'inactive') return 'Không hoạt động';
    if (statusKey === 'pending') return 'Chờ duyệt';
    if (isLikelyId(statusKey)) return '-';
    return statusKey || '-';
  };

  const getStatusColor = (row: EmployeeListRow): string => {
    const directColor = String(row.statusColor || '').trim();
    if (directColor) {
      return directColor;
    }

    const statusKey = getStatusKey(row);
    const mappedColor = statusMetaById[statusKey]?.color;
    if (mappedColor) {
      return mappedColor;
    }

    switch (statusKey) {
      case 'active':
        return '#16a34a';
      case 'inactive':
        return '#f97316';
      case 'pending':
        return '#f59e0b';
      default:
        return '#6b7280';
    }
  };

  const statusMetaRequestedRef = React.useRef(false);

  useEffect(() => {
    const needsMeta = displayEmployees.some((row) => {
      if (String(row.statusName || '').trim()) {
        return false;
      }
      const key = getStatusKey(row);
      return isLikelyId(key) && !statusMetaById[key];
    });

    if (!needsMeta || statusMetaRequestedRef.current) {
      return;
    }

    statusMetaRequestedRef.current = true;
    let cancelled = false;

    getStatusMetaByModule(EMPLOYEE_STATUS_MODULES)
      .then((map) => {
        if (!cancelled) {
          setStatusMetaById(map);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStatusMetaById({});
        }
      });

    return () => {
      cancelled = true;
    };
  }, [displayEmployees, statusMetaById]);

  const columns = [
    {
      id: 'code',
      label: 'Mã NV',
      width: '96px',
      render: (_value: unknown, employee: EmployeeDataRow) => {
        if (!employee) return renderFallback();
        if (isGroupRow(employee)) {
          return (
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>
              {employee.departmentLabel} ({employee.rowCount})
            </Typography>
          );
        }
        return (
          <Typography variant="body2" sx={sharedListStyles.cellText}>
            {employee.code || '-'}
          </Typography>
        );
      },
    },
    {
      id: 'name',
      label: 'Họ và Tên',
      width: '180px',
      render: (_value: unknown, employee: EmployeeDataRow) => {
        if (!employee) return renderFallback();
        if (isGroupRow(employee)) return null;
        return (
          <EmployeeDisplay
            name={employee.name}
            nickname={employee.nickname}
            avatar={employee.avatar}
            image1={employee.image1 || null}
            employeeId={employee.id}
            fallbackText="-"
          />
        );
      },
    },
    {
      id: 'nickname',
      label: 'Bi Danh',
      width: '120px',
      render: (_value: unknown, employee: EmployeeDataRow) => {
        if (!employee) return renderFallback();
        if (isGroupRow(employee)) return null;
        return (
          <Typography variant="body2" sx={sharedListStyles.cellText}>
            {employee.nickname || '-'}
          </Typography>
        );
      },
    },
    {
      id: 'phone',
      label: 'SĐT',
      width: '112px',
      render: (_value: unknown, employee: EmployeeDataRow) => {
        if (!employee) return renderFallback();
        if (isGroupRow(employee)) return null;
        return (
          <Typography variant="body2" sx={sharedListStyles.cellText}>
            {employee.phone || '-'}
          </Typography>
        );
      },
    },
    {
      id: 'email',
      label: 'Email',
      width: '180px',
      render: (_value: unknown, employee: EmployeeDataRow) => {
        if (!employee) return renderFallback();
        if (isGroupRow(employee)) return null;
        return (
          <Typography variant="body2" sx={sharedListStyles.cellText}>
            {employee.email || '-'}
          </Typography>
        );
      },
    },
    {
      id: 'missions',
      label: 'Nhiệm vụ',
      width: '150px',
      render: (_value: unknown, employee: EmployeeDataRow) => {
        if (!employee) return renderFallback();
        if (isGroupRow(employee)) return null;
        const missionMap = new Map(MISSIONS_OPTIONS.map((m) => [m.code, m.label]));
        const labels = (employee.missions || []).map((code) => missionMap.get(code) || code);
        return (
          <Typography variant="body2" sx={sharedListStyles.cellText}>
            {labels.length ? labels.join(', ') : '-'}
          </Typography>
        );
      },
    },
    {
      id: 'status',
      label: 'Trạng thái',
      width: '128px',
      render: (_value: unknown, employee: EmployeeDataRow) => {
        if (!employee) return renderFallback();
        if (isGroupRow(employee)) return null;
        const label = getStatusLabel(employee);
        return (
          <Typography
            variant="body2"
            sx={{
              ...sharedListStyles.cellText,
              color: getStatusColor(employee),
              fontWeight: 500,
            }}
          >
            {label}
          </Typography>
        );
      },
    },
    {
      id: 'startDate',
      label: 'Ngày vào làm',
      width: '124px',
      render: (_value: unknown, employee: EmployeeDataRow) => {
        if (!employee) return renderFallback();
        if (isGroupRow(employee)) return null;
        return (
          <Typography variant="body2" sx={sharedListStyles.cellText}>
            {formatStartDate(employee.startDate)}
          </Typography>
        );
      },
    },
    {
      id: 'jobTitle',
      label: 'Chức vụ',
      width: '136px',
      render: (_value: unknown, employee: EmployeeDataRow) => {
        if (!employee) return renderFallback();
        if (isGroupRow(employee)) return null;
        const resolvedJobTitle = String(
          employee.jobTitle ||
          (employee as any)?.job_title_name ||
          (employee as any)?.job_title?.name ||
          (employee as any)?.job_title ||
          ''
        ).trim();
        return (
          <Typography variant="body2" sx={sharedListStyles.cellText}>
            {resolvedJobTitle || '-'}
          </Typography>
        );
      },
    }
  ];

  useEffect(() => {
    const currentIds = new Set(displayEmployees.map((emp) => emp.id));
    setSelectedIds((prev) => prev.filter((id) => currentIds.has(id)));
  }, [displayEmployees]);

  useEffect(() => {
    onSelectionChange?.(selectedIds);
  }, [onSelectionChange, selectedIds]);

  const areAllRowsSelected = useMemo(
    () => displayEmployees.length > 0 && selectedIds.length === displayEmployees.length,
    [displayEmployees.length, selectedIds.length]
  );
  const areSomeRowsSelected = useMemo(
    () => selectedIds.length > 0 && selectedIds.length < displayEmployees.length,
    [displayEmployees.length, selectedIds.length]
  );

  const handleEmployeeSelect = (employeeId: string) => {
    setSelectedIds((prev) =>
      prev.includes(employeeId) ? prev.filter((id) => id !== employeeId) : [...prev, employeeId]
    );
  };

  const handleSelectAll = () => {
    if (areAllRowsSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(displayEmployees.map((emp) => emp.id));
  };

  const openDeleteConfirm = useCallback(
    (employeeIds: string[]) => {
      if (!effectiveCanDelete) {
        onDeleteError?.('Bạn không có quyền xóa nhân viên');
        return;
      }
      if (employeeIds.length === 0) {
        onDeleteError?.('Vui lòng chọn ít nhất một nhân viên để xóa');
        return;
      }
      setShowDeleteConfirm(true);
    },
    [effectiveCanDelete, onDeleteError]
  );

  const handleToolbarDelete = useCallback(() => {
    openDeleteConfirm(selectedIds);
  }, [openDeleteConfirm, selectedIds]);

  useImperativeHandle(ref, () => ({ requestDelete: handleToolbarDelete }), [handleToolbarDelete]);

  const handleConfirmDelete = async () => {
    if (!effectiveCanDelete) {
      onDeleteError?.('Bạn không có quyền xóa nhân viên');
      setShowDeleteConfirm(false);
      return;
    }

    setShowDeleteConfirm(false);
    setIsDeleting(true);
    startGlobalLoading(`Đang xóa ${selectedIds.length} nhân viên...`, 'employees-delete');

    try {
      await softDeleteEmployees(selectedIds);
      setSelectedIds([]);
      onDeleted?.();
      onDeleteSuccess?.(`Đã xóa ${selectedIds.length} nhân viên thành công!`);
    } catch (error) {
      onDeleteError?.(formatErrorForSnackbar(error as Error) || 'Có lỗi xảy ra khi xóa nhân viên!');
    } finally {
      setIsDeleting(false);
      stopGlobalLoading('employees-delete');
    }
  };

  const handleRowClickInternal = (employee: EmployeeListRow) => {
    if (!effectiveCanRead) {
      onReadDenied?.('Bạn không có quyền xem chi tiết nhân viên');
      return;
    }
    onRowClick(employee);
  };

  const totalCount = pagination?.count || 0;
  const totalPages = pagination?.totalPages || 1;
  const currentPage = pagination?.page || 1;
  const pageSize = pagination?.pageSize || employees.length || DEFAULT_PAGE_SIZE;
  useEffect(() => {
    if (selectedIds.length === 0 && bulkAction) {
      setBulkAction('');
    }
  }, [bulkAction, selectedIds.length]);

  const handleApplyBulkAction = async () => {
    if (!effectiveCanDelete || !bulkAction || selectedIds.length === 0) {
      return;
    }
    if (bulkAction === 'delete') {
      openDeleteConfirm(selectedIds);
      setBulkAction('');
    }
  };

  return (
    <Box sx={sharedListStyles.container}>
      <DataTableModule
        data={tableRows}
        leftFilterWidth={0}
        tableContainerSx={{ maxHeight: 'none' }}
        tableLayout="fixed"
        checkboxColumnWidth={36}
        columns={columns}
        hideCheckboxForRow={(row: EmployeeDataRow) => isGroupRow(row)}
        onRowSelect={handleEmployeeSelect}
        onSelectAll={handleSelectAll}
        onRowClick={(row: EmployeeDataRow) => {
          if (isGroupRow(row)) return;
          handleRowClickInternal(row);
        }}
        getRowId={(row: EmployeeDataRow) => row.id}
        isRowSelected={(row: EmployeeDataRow) => !isGroupRow(row) && selectedIds.includes(row.id)}
        areAllRowsSelected={areAllRowsSelected}
        areSomeRowsSelected={areSomeRowsSelected}
        getRowClassName={(row: EmployeeDataRow) => (isGroupRow(row) ? 'group-row' : '')}
      />

      {selectedIds.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            px: 0,
            py: 2,
            boxSizing: 'border-box',
            gap: 2,
          }}
        >
          {effectiveCanDelete ? (
            <AppBulkActions
              selectedCount={selectedIds.length}
              itemLabel='nhân viên'
              action={bulkAction}
              loading={applyingAction}
              actions={[
                { value: 'delete', label: 'Xóa' },
              ]}
              onActionChange={setBulkAction}
              onApply={() => { void handleApplyBulkAction(); }}
            />
          ) : <Box />}
          {totalCount > 0 && (
            <AppPagination
              count={totalCount}
              page={currentPage}
              pageSize={pageSize}
              totalPages={totalPages}
              itemLabel='nhân viên'
              inline
              containerSx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          )}
        </Box>
      )}

      {selectedIds.length === 0 && (
        <AppPagination
          count={totalCount}
          page={currentPage}
          pageSize={pageSize}
          totalPages={totalPages}
          itemLabel="nhân viên"
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}

      <ConfirmationModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => { void handleConfirmDelete(); }}
        title='Xóa nhân viên'
        message={`Bạn có chắc chắn muốn xóa ${selectedIds.length} nhân viên đã chọn?`}
        confirmText='Xóa'
        cancelText='Hủy'
        isLoading={isDeleting}
      />
    </Box>
  );
});

export default MainListEmployee;

